/* ============================================================================
 * QrCodeBlock — Themed QR code with optional caption + download action.
 *
 * Renders a high-contrast QR using qrcode.react in the brand palette
 * (#0f0e0c background → #CEB584 gold modules), with an optional download-as-PNG
 * button that flushes the on-screen <canvas> to a Blob.
 *
 * Used from:
 *   • Settings → Company tab (vCard-style company contact QR)
 *   • Client Portal → contractor side (per-portal access link QR)
 *
 * Props:
 *   - value:        string to encode (URL or vCard text)
 *   - title:        small caption above the QR
 *   - subtitle:     muted helper text below the QR
 *   - downloadName: filename when user clicks "Download PNG"
 *   - size:         pixel size of the QR (default 192)
 *   - testId:       data-testid root (children get suffixed)
 * ========================================================================== */

import React, { useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Download, QrCode } from 'lucide-react';
import { cn } from '@/lib/utils';

const GOLD = '#CEB584';
const BG = '#0f0e0c';

export const QrCodeBlock = ({
  value,
  title,
  subtitle,
  downloadName = 'qr.png',
  size = 192,
  testId,
  className,
  children,
}) => {
  const containerRef = useRef(null);

  const handleDownload = () => {
    if (!containerRef.current) return;
    const canvas = containerRef.current.querySelector('canvas');
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Free the URL on the next tick.
      setTimeout(() => URL.revokeObjectURL(url), 250);
    });
  };

  if (!value) {
    return (
      <div
        className={cn(
          'rounded-md border border-[hsl(var(--hairline))] bg-[hsl(var(--muted))] p-4 text-xs text-muted-foreground',
          className
        )}
        data-testid={testId}
      >
        Add the required info to generate a QR code.
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-md border border-[hsl(var(--hairline))] bg-[hsl(var(--card))] p-4',
        className
      )}
      data-testid={testId}
    >
      <div className="flex items-start gap-2 mb-3">
        <QrCode className="h-3.5 w-3.5 text-[hsl(var(--accent))] mt-0.5 flex-shrink-0" aria-hidden />
        <div className="flex-1 min-w-0">
          {title ? (
            <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-foreground">
              {title}
            </div>
          ) : null}
          {subtitle ? (
            <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
          ) : null}
        </div>
      </div>

      <div
        ref={containerRef}
        className="flex flex-col items-center gap-3 sm:flex-row sm:items-start"
      >
        <div
          className="rounded-md p-3 bg-[#0f0e0c] border border-[hsl(var(--accent))]/20 flex items-center justify-center"
          data-testid={testId ? `${testId}-canvas-wrap` : undefined}
        >
          <QRCodeCanvas
            value={value}
            size={size}
            bgColor={BG}
            fgColor={GOLD}
            level="M"
            includeMargin={false}
            data-testid={testId ? `${testId}-canvas` : undefined}
          />
        </div>
        <div className="flex-1 w-full sm:pt-1">
          {children}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleDownload}
            className="mt-3 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 text-xs uppercase tracking-[0.16em]"
            data-testid={testId ? `${testId}-download` : undefined}
          >
            <Download className="h-3.5 w-3.5 mr-2" />
            Download PNG
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QrCodeBlock;

/* Build a minimal vCard 3.0 string from a company info object. */
export const buildCompanyVCard = (company = {}) => {
  const lines = ['BEGIN:VCARD', 'VERSION:3.0'];
  const name = company.name || company.legal_name || '';
  if (name) {
    lines.push(`FN:${name}`);
    lines.push(`N:${name};;;;`);
    lines.push(`ORG:${name}`);
  }
  if (company.tagline) lines.push(`TITLE:${company.tagline}`);
  if (company.phone) lines.push(`TEL;TYPE=WORK,VOICE:${company.phone}`);
  if (company.email) lines.push(`EMAIL;TYPE=WORK:${company.email}`);
  if (company.website) lines.push(`URL:${company.website}`);
  if (company.address)
    lines.push(`ADR;TYPE=WORK:;;${company.address.replace(/\n/g, ', ')};;;;`);
  if (company.license) lines.push(`NOTE:License: ${company.license}`);
  lines.push('END:VCARD');
  return lines.join('\n');
};
